import axios, { AxiosError } from 'axios';
import { getRefreshToken, setRefreshToken, clearRefreshToken } from './auth';
import { setAuthToken } from './api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

export const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

/**
 * O interceptor global do backend envelopa respostas de lista como
 * { version, _links, data: [...] }. Aceita os dois formatos para nao quebrar
 * caso a rota responda o array direto.
 */
export function unwrapList<T>(body: T[] | { data?: T[] } | null | undefined): T[] {
  if (Array.isArray(body)) return body;
  const data = body?.data;
  return Array.isArray(data) ? data : [];
}

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- Refresh token interceptor ---

let isRefreshing = false;
let failedQueue: Array<{ resolve: (token: string) => void; reject: (err: unknown) => void }> = [];

function processQueue(error: unknown, token: string | null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token!);
  });
  failedQueue = [];
}

let onSessionExpired: (() => void) | null = null;

export function setSessionExpiredHandler(handler: () => void) {
  onSessionExpired = handler;
}

async function doRefresh(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
  const response = await fetch(`${BASE_URL}/public/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  const result = await response.json();
  if (!response.ok || result.error || !result.data) {
    throw new Error(result.error ?? 'Falha ao renovar sessão');
  }
  return result.data;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      onSessionExpired?.();
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      })
        .then((token) => {
          originalRequest.headers!['Authorization'] = `Bearer ${token}`;
          return api(originalRequest);
        })
        .catch((err) => Promise.reject(err));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      const tokens = await doRefresh(refreshToken);
      setAuthToken(tokens.token);
      setRefreshToken(tokens.refreshToken);
      api.defaults.headers.common['Authorization'] = `Bearer ${tokens.token}`;
      processQueue(null, tokens.token);
      originalRequest.headers!['Authorization'] = `Bearer ${tokens.token}`;
      return api(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      clearRefreshToken();
      setAuthToken(null);
      onSessionExpired?.();
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);
